import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Camera, CheckCircle2, CreditCard, Inbox as InboxIcon, Loader2, LogOut, RefreshCw } from "lucide-react";
import { Logo } from "./Logo";
import { checkOwner, getMe } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  accepted: "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30",
  denied: "bg-red-500/15 text-red-300 border border-red-400/30",
  cancelled: "bg-slate-400/15 text-slate-300 border border-slate-400/30",
  pending: "bg-amber-400/15 text-amber-300 border border-amber-400/30",
};

const STATUS_LABELS = { accepted: "Accepted", denied: "Denied", cancelled: "Cancelled", pending: "Pending" };

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function MyGames() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [name, setName] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const fetchBookings = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/my/bookings`, { withCredentials: true });
      setBookings(data);
      setLoaded(true);
      setErr("");
    } catch {
      setLoaded(true);
      setErr("Couldn't load your games — tap refresh to try again.");
    }
    setBusy(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const owner = await checkOwner();
      const me = await getMe();
      if (cancelled) return;
      if (!me) {
        navigate("/signin");
        return;
      }
      if (owner) setIsOwner(true);
      setName(me.name || "");
      await fetchBookings();
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchBookings, navigate]);

  const rebook = (b) => {
    sessionStorage.setItem(
      "gl_rebook",
      JSON.stringify({
        name: b.name,
        email: b.email,
        phone: b.phone,
        package: b.package,
        team: b.team,
        league: b.league,
        field_location: b.field_location,
      })
    );
    window.location.href = "/#booking";
  };

  const payNow = async (b, kind = "deposit") => {
    const key = `${b.id}:${kind}`;
    if (payingId) return;
    setPayingId(key);
    setErr("");
    try {
      const { data } = await axios.post(
        `${API}/my/payments/checkout`,
        { booking_id: b.id, kind, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't start the payment — please try again.");
      setPayingId(null);
    }
  };

  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    window.location.href = "/";
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink font-body text-white" data-testid="my-games-page">
        <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
          <Loader2 className="h-5 w-5 animate-spin text-accentlight" /> Loading your games…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink font-body text-white" data-testid="my-games-page">
      <header className="border-b border-white/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="text-accentlight">
              <Logo className="h-8 w-8" />
            </span>
            <div className="leading-none">
              <p className="font-display text-xl tracking-wider">
                MY <span className="text-accentlight">GAMES</span>
              </p>
              <p className="text-[9px] font-semibold uppercase tracking-[0.4em] text-white/40">
                GameLens Photography
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={fetchBookings}
              disabled={busy}
              data-testid="my-games-refresh"
              aria-label="Refresh"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight disabled:opacity-60 sm:px-4"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={signOut}
              data-testid="my-games-signout"
              aria-label="Sign out"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight sm:px-4"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        {isOwner && (
          <div
            className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-accentlight/30 bg-surface p-6"
            data-testid="owner-banner"
          >
            <p className="flex items-center gap-3 text-sm text-white/70">
              <Camera className="h-5 w-5 text-accentlight" />
              You're signed in as the photographer — your studio lives here:
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/inbox"
                data-testid="owner-inbox-link"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110"
              >
                <InboxIcon className="h-4 w-4" /> Booking inbox
              </a>
              <a
                href="/?manage"
                data-testid="owner-schedule-link"
                className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10"
              >
                <CalendarDays className="h-4 w-4" /> Schedule
              </a>
            </div>
          </div>
        )}

        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl tracking-wider md:text-5xl">
              {name ? `HEY, ${name.split(" ")[0].toUpperCase()}` : "YOUR GAMES"}
            </h1>
            <p className="mt-2 text-sm text-white/50">
              Every booking request you've sent — newest first.
            </p>
          </div>
          <button
            onClick={() => (bookings[0] ? rebook(bookings[0]) : (window.location.href = "/#booking"))}
            data-testid="my-games-book-cta"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110"
          >
            <Camera className="h-4 w-4" /> Book another game
          </button>
        </div>

        {err && (
          <div
            className="mb-6 flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
            data-testid="my-games-error"
          >
            {err}
          </div>
        )}

        {loaded && bookings.length === 0 && (
          <div
            className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/10 py-20 text-center"
            data-testid="my-games-empty"
          >
            <Camera className="h-10 w-10 text-white/20" strokeWidth={1.5} />
            <p className="text-white/50">No games on the books yet.</p>
            <p className="text-xs text-white/30">Book your first one — takes about a minute.</p>
            <a
              href="/#booking"
              className="rounded-full bg-gradient-to-r from-accent to-accentlight px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110"
            >
              Book a game
            </a>
          </div>
        )}

        <div className="space-y-4">
          {bookings.map((b) => (
            <article
              key={b.id}
              data-testid={`game-card-${b.id}`}
              className={`rounded-2xl border border-white/5 bg-surface p-6 md:p-8 ${b.status === "denied" || b.status === "cancelled" ? "opacity-55" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl tracking-wider" data-testid={`game-team-${b.id}`}>
                    {b.team}
                  </h2>
                  <p className="mt-1 text-xs text-white/40">Requested {fmtDay(b.created_at)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`rounded-full px-4 py-1.5 text-xs font-bold ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`}
                    data-testid={`game-status-${b.id}`}
                  >
                    {STATUS_LABELS[b.status] || "Pending"}
                  </span>
                  {b.package && (
                    <span
                      className="rounded-full bg-gradient-to-r from-accent/20 to-accentlight/20 px-4 py-1.5 text-xs font-bold text-accentlight"
                      data-testid={`game-package-${b.id}`}
                    >
                      {b.package}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                {b.game_date && (
                  <p className="text-white/70">
                    Game date: <span className="font-semibold text-white">{b.game_date}</span>
                    {b.game_time ? ` · ${b.game_time}` : ""}
                  </p>
                )}
                {b.league && (
                  <p className="text-white/70">
                    League: <span className="font-semibold text-white">{b.league}</span>
                  </p>
                )}
                {b.field_location && (
                  <p className="text-white/70">
                    Field: <span className="font-semibold text-white">{b.field_location}</span>
                  </p>
                )}
              </div>

              {b.status === "accepted" && (
                <p className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300" data-testid={`game-accepted-note-${b.id}`}>
                  <CheckCircle2 className="h-4 w-4" /> You're on the schedule — confirmation is in your email
                </p>
              )}

              {b.status === "accepted" && (
                <div className="mt-5 flex flex-wrap items-center gap-3" data-testid={`game-payments-${b.id}`}>
                  {b.deposit_paid ? (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-4 py-1.5 text-xs font-bold text-emerald-300 border border-emerald-400/30"
                      data-testid={`game-deposit-${b.id}`}
                    >
                      Deposit received
                    </span>
                  ) : (
                    <button
                      onClick={() => payNow(b, "deposit")}
                      disabled={payingId !== null}
                      data-testid={`game-pay-deposit-${b.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {payingId === `${b.id}:deposit` ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening Stripe&hellip;
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-3.5 w-3.5" /> Pay ${Number(b.deposit_due || 0).toFixed(2)} deposit
                        </>
                      )}
                    </button>
                  )}
                  {!b.balance_paid && b.balance_due > 0 && b.balance_invoice_sent ? (
                    <button
                      onClick={() => payNow(b, "balance")}
                      disabled={payingId !== null}
                      data-testid={`game-pay-balance-${b.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {payingId === `${b.id}:balance` ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening Stripe&hellip;
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-3.5 w-3.5" /> Pay ${Number(b.balance_due || 0).toFixed(2)} balance
                        </>
                      )}
                    </button>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold ${
                        b.balance_paid
                          ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                          : "border border-white/10 bg-surface-hover text-white/60"
                      }`}
                      data-testid={`game-balance-${b.id}`}
                    >
                      {b.balance_paid ? "Balance received" : "Balance after photos"}
                    </span>
                  )}
                </div>
              )}

              {b.message && (
                <p className="mt-4 rounded-xl bg-ink p-4 text-sm leading-relaxed text-white/70" data-testid={`game-notes-${b.id}`}>
                  <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">Notes</span>
                  {b.message}
                </p>
              )}

              <button
                onClick={() => rebook(b)}
                data-testid={`game-rebook-${b.id}`}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10"
              >
                Book again with these details
              </button>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}